import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PauseIcon from '@mui/icons-material/Pause';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';

import Keypad from '~components/Keypad';
import useDebounce from '~hooks/useDebounce';
import useExternalTransfers, { ExternalTransferEntry } from '~hooks/useExternalTransfers';

// --- Types ---

export type CustomerChannelDisplay = {
  key: string;
  address: string;
  state: string;
  since: string;
  isMuted: boolean;
};

export type AgentChannelDisplay = {
  key: string;
  username: string;
  state: string;
  since: string;
};

// --- ElapsedTime ---

/** ISO timestamp → "M:SS" elapsed time, self-updating every second (mirrors timeFrom in App.svelte) */
const ElapsedTime = ({ since }: { since: string }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, []);

  if (!since) return null;
  const elapsed = Math.round((Date.now() - new Date(since).getTime()) / 1000);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return <Typography variant='caption'>{`${mins}:${secs < 10 ? '0' : ''}${secs}`}</Typography>;
};

export type AgentStateOption = {
  value: string;
  label: string;
  status: string;
};

// --- Styled ---

const ControlButton = styled(Button)({
  'height': 40,
  'borderRadius': 4,
  'textTransform': 'none',
  'fontWeight': 400,
  'color': '#fff',
  ':disabled': {
    color: 'rgba(255, 255, 255, 0.5)',
  },
});

// --- Helpers ---

const getChannelStateColor = (state: string): { bg: string; color: string } => {
  switch (state) {
    case 'Ringing':
      return { bg: '#1976d2', color: '#fff' };
    case 'Connected':
      return { bg: '#2e7d32', color: '#fff' };
    case 'On Hold':
      return { bg: '#ed6c02', color: '#000' };
    case 'Disconnected':
      return { bg: '#d32f2f', color: '#fff' };
    case 'Transferred':
    case 'Transfer Completed':
      return { bg: '#e0e0e0', color: '#000' };
    default:
      return { bg: '#9e9e9e', color: '#fff' };
  }
};

const getAgentStatusColor = (status: string): string => {
  switch (status) {
    case 'In Queue':
      return '#2e7d32';
    case 'Unavailable':
      return '#424242';
    default:
      return '#d32f2f';
  }
};

const hasChannelActions = (state: string): boolean =>
  state === 'On Hold' || state === 'Connected' || state === 'Ringing';

// --- Props ---

type Props = {
  // Data
  username: string;
  state: string;
  agentMuted: boolean;
  isRecording: boolean;
  canPauseCallRecordings: boolean;
  canLeave: boolean;
  canConference: boolean;
  canMute: boolean;
  customerChannels: CustomerChannelDisplay[];
  agentChannels: AgentChannelDisplay[];
  agentStates: AgentStateOption[];
  // Actions
  onSendDtmf: (digit: string) => void;
  onLeaveCall: () => void;
  onSetAgentMuted: (muted: boolean) => void;
  onSetRecording: (recording: boolean) => void;
  onInternalConference: (username: string) => void;
  onExternalConference: (endpoint: string) => void;
  onChannelAction: (channelKey: string, action: string) => void;
  onRemoveAgentFromConference: (username: string) => void;
};

// --- Component ---

export const ControlCard = ({
  username,
  state,
  agentMuted,
  isRecording,
  canPauseCallRecordings,
  canLeave,
  canConference,
  canMute,
  customerChannels,
  agentChannels,
  agentStates,
  onSendDtmf,
  onLeaveCall,
  onSetAgentMuted,
  onSetRecording,
  onInternalConference,
  onExternalConference,
  onChannelAction,
  onRemoveAgentFromConference,
}: Props) => {
  const [selectedAgent, setSelectedAgent] = useState('');
  const [externalEndpoint, setExternalEndpoint] = useState('');
  const [selectedOption, setSelectedOption] = useState<ExternalTransferEntry | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const isAfterCallWork = state === 'AfterCallWork';
  const selectedAgentOption = agentStates.find((a) => a.value === selectedAgent);
  const disableConferenceAgent = !canConference || !selectedAgent || selectedAgentOption?.status !== 'In Queue';
  const { list: externalTransferList, getNextPage } = useExternalTransfers({ search: debouncedSearch });
  const scrollRef = useRef(0);
  const listboxRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    if (listboxRef.current) {
      listboxRef.current.scrollTop = scrollRef.current;
    }
  }, [externalTransferList]);

  return (
    <Card elevation={1} sx={{ mt: 2 }}>
      <CardContent sx={{ ':last-child': { paddingBottom: 2 } }}>
        {/* DTMF Keypad */}
        <Keypad disabled={isAfterCallWork} onKeyClick={onSendDtmf} />

        {/* Leave Call */}
        <ControlButton
          fullWidth
          variant='contained'
          disableElevation
          disabled={!canLeave}
          startIcon={<PhoneForwardedIcon />}
          sx={{ 'mt': 1.5, 'backgroundColor': '#4A90D9', ':hover': { backgroundColor: '#3A7BC8' } }}
          onClick={onLeaveCall}>
          Leave Call
        </ControlButton>

        {/* Mute / Recording — hidden in AfterCallWork */}
        {!isAfterCallWork && (
          <>
            {/* Agent Mute Toggle */}
            {agentMuted ? (
              <ControlButton
                fullWidth
                variant='contained'
                disableElevation
                disabled={!canMute}
                startIcon={<MicIcon />}
                sx={{ 'mt': 1, 'backgroundColor': '#555555', ':hover': { backgroundColor: '#444444' } }}
                onClick={() => onSetAgentMuted(false)}>
                Unmute
              </ControlButton>
            ) : (
              <ControlButton
                fullWidth
                variant='contained'
                disableElevation
                disabled={!canMute}
                startIcon={<MicOffIcon />}
                sx={{ 'mt': 1, 'backgroundColor': '#555555', ':hover': { backgroundColor: '#444444' } }}
                onClick={() => onSetAgentMuted(true)}>
                Mute
              </ControlButton>
            )}

            {/* Pause / Resume Recording */}
            {canPauseCallRecordings &&
              (isRecording ? (
                <ControlButton
                  fullWidth
                  variant='contained'
                  disableElevation
                  disabled={!canMute}
                  startIcon={<PauseCircleIcon />}
                  sx={{ 'mt': 1, 'backgroundColor': '#555555', ':hover': { backgroundColor: '#444444' } }}
                  onClick={() => onSetRecording(false)}>
                  Pause Recording
                </ControlButton>
              ) : (
                <ControlButton
                  fullWidth
                  variant='contained'
                  disableElevation
                  disabled={!canMute}
                  startIcon={<FiberManualRecordIcon />}
                  sx={{ 'mt': 1, 'backgroundColor': '#555555', ':hover': { backgroundColor: '#444444' } }}
                  onClick={() => onSetRecording(true)}>
                  Resume Recording
                </ControlButton>
              ))}
          </>
        )}

        {/* Add Call Section */}
        <Typography variant='body2' sx={{ mt: 2 }}>
          Add Call: <Typography variant='caption'>(Calls must be on hold before others can be added)</Typography>
        </Typography>

        {/* Internal Conference */}
        <Typography variant='caption' display='block' sx={{ mt: 1 }}>
          Internal:
        </Typography>
        <Box display='flex' gap={0.5}>
          <Autocomplete
            fullWidth
            size='small'
            options={agentStates}
            value={agentStates.find((a) => a.value === selectedAgent) || null}
            onChange={(_, option) => setSelectedAgent(option ? option.value : '')}
            disabled={!canConference}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
                  <Typography variant='body2'>{option.label}</Typography>
                  <Chip
                    label={option.status}
                    size='small'
                    sx={{
                      ml: 1,
                      backgroundColor: getAgentStatusColor(option.status),
                      color: '#fff',
                      fontSize: 11,
                      height: 20,
                    }}
                  />
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder='Select...' sx={{ fontSize: 14 }} />}
          />
          <IconButton
            size='small'
            disabled={disableConferenceAgent}
            onClick={() => {
              onInternalConference(selectedAgent);
              setSelectedAgent('');
            }}
            sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: 1 }}>
            <PhoneIcon fontSize='small' />
          </IconButton>
        </Box>

        {/* External Conference */}
        <Typography variant='caption' display='block' sx={{ mt: 1 }}>
          External:
        </Typography>
        <Box display='flex' gap={0.5}>
          <Autocomplete
            fullWidth
            size='small'
            freeSolo
            options={externalTransferList}
            filterOptions={(x) => x}
            getOptionLabel={(opt: string | ExternalTransferEntry) =>
              typeof opt === 'string' ? opt : `${opt.name} (${opt.phoneNumber})`
            }
            ListboxProps={{
              style: { maxHeight: 200 },
              onScroll: (e: React.SyntheticEvent) => {
                const listbox = e.currentTarget as HTMLUListElement;
                listboxRef.current = listbox;
                scrollRef.current = listbox.scrollTop;
                if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 10) {
                  getNextPage();
                }
              },
            }}
            inputValue={externalEndpoint}
            onInputChange={(_, value, reason) => {
              if (reason !== 'reset') {
                setSearch(value);
                setExternalEndpoint(value);
              }
            }}
            value={selectedOption}
            onChange={(_, opt) => {
              if (opt && typeof opt !== 'string') {
                setExternalEndpoint(opt.value);
                setSelectedOption(opt);
              }
            }}
            disabled={!canConference}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
                  <Typography variant='body2'>{option.name}</Typography>
                  <Chip label={option.phoneNumber} size='small' sx={{ ml: 1, fontSize: 11, height: 20 }} />
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder='e.g. +61234567890' sx={{ fontSize: 14 }} />}
          />
          <IconButton
            size='small'
            disabled={!canConference || !externalEndpoint}
            onClick={() => {
              if (externalEndpoint) {
                onExternalConference(externalEndpoint);
                setExternalEndpoint('');
                setSelectedOption(null);
              }
            }}
            sx={{ border: '1px solid rgba(0, 0, 0, 0.23)', borderRadius: 1 }}>
            <PhoneIcon fontSize='small' />
          </IconButton>
        </Box>

        {/* Channel Cards — hidden in AfterCallWork */}
        {!isAfterCallWork && (
          <>
            {/* Customer Channels */}
            {customerChannels.map((channel) => {
              const stateColor = getChannelStateColor(channel.state);
              return (
                <Card key={channel.key} variant='outlined' sx={{ mt: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: stateColor.bg,
                      color: stateColor.color,
                      px: 1.5,
                      py: 0.75,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <Typography variant='body2' fontWeight={600}>
                      {channel.address}: {channel.state}
                    </Typography>
                    <ElapsedTime since={channel.since} />
                  </Box>

                  {hasChannelActions(channel.state) && (
                    <Box display='flex' gap={0.5} p={1}>
                      {channel.state === 'On Hold' ? (
                        <ControlButton
                          fullWidth
                          variant='contained'
                          disableElevation
                          size='small'
                          startIcon={<PlayArrowIcon />}
                          sx={{
                            'fontSize': '11px',
                            'backgroundColor': '#1976d2',
                            ':hover': { backgroundColor: '#1565c0' },
                            '& .MuiButton-startIcon': {
                              marginRight: 0,
                            },
                          }}
                          onClick={() => onChannelAction(channel.key, 'stopHold')}>
                          Take Off Hold
                        </ControlButton>
                      ) : (
                        <ControlButton
                          fullWidth
                          variant='contained'
                          disableElevation
                          size='small'
                          disabled={channel.isMuted || channel.state !== 'Connected'}
                          startIcon={<PauseIcon />}
                          sx={{ 'backgroundColor': '#E6A817', ':hover': { backgroundColor: '#D49A10' } }}
                          onClick={() => onChannelAction(channel.key, 'hold')}>
                          Hold
                        </ControlButton>
                      )}

                      {channel.isMuted ? (
                        <ControlButton
                          fullWidth
                          variant='contained'
                          disableElevation
                          size='small'
                          startIcon={<VolumeUpIcon />}
                          sx={{ 'backgroundColor': '#1976d2', ':hover': { backgroundColor: '#1565c0' } }}
                          onClick={() => onChannelAction(channel.key, 'unmute')}>
                          Unmute
                        </ControlButton>
                      ) : (
                        <ControlButton
                          fullWidth
                          variant='contained'
                          disableElevation
                          size='small'
                          startIcon={<VolumeOffIcon />}
                          sx={{ 'backgroundColor': '#555555', ':hover': { backgroundColor: '#444444' } }}
                          onClick={() => onChannelAction(channel.key, 'mute')}>
                          Mute
                        </ControlButton>
                      )}

                      <ControlButton
                        fullWidth
                        variant='contained'
                        disableElevation
                        size='small'
                        startIcon={<PhoneDisabledIcon />}
                        sx={{ 'backgroundColor': '#C0392B', ':hover': { backgroundColor: '#A93226' } }}
                        onClick={() => onChannelAction(channel.key, 'hangup')}>
                        Hangup
                      </ControlButton>
                    </Box>
                  )}
                </Card>
              );
            })}

            {/* Agent Channels (excluding self) */}
            {agentChannels
              .filter((channel) => channel.username !== username)
              .map((channel) => {
                const stateColor = getChannelStateColor(channel.state);
                return (
                  <Card key={channel.key} variant='outlined' sx={{ mt: 1.5 }}>
                    <Box
                      sx={{
                        backgroundColor: stateColor.bg,
                        color: stateColor.color,
                        px: 1.5,
                        py: 0.75,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}>
                      <Typography variant='body2' fontWeight={600}>
                        {channel.username}: {channel.state}
                      </Typography>
                      <ElapsedTime since={channel.since} />
                    </Box>

                    {hasChannelActions(channel.state) && (
                      <Box display='flex' gap={0.5} p={1}>
                        <ControlButton
                          fullWidth
                          variant='contained'
                          disableElevation
                          size='small'
                          startIcon={<PhoneDisabledIcon />}
                          sx={{ 'backgroundColor': '#C0392B', ':hover': { backgroundColor: '#A93226' } }}
                          onClick={() => onRemoveAgentFromConference(channel.username)}>
                          Hangup
                        </ControlButton>
                      </Box>
                    )}
                  </Card>
                );
              })}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ControlCard;
